import { Injectable } from '@angular/core';

import { TennisPointsVisibilityService } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EventModel, FootballScore, IcehockeyPeriodGameScore, ScoreboardType } from '@frontend/sports/betting-offer/feature/model';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';

import { MyBetsEventModel } from '../../my-bets/models/my-bets-event.model';
import { BaseBallscoreboardViewModel } from './baseball-scoreboard.viewmodel';
import { DartsScoreboardViewModel } from './darts-scoreboard.viewmodel';
import { ScoreboardLeaderboardGameViewModel } from './leaderboard-game-scoreboard.viewmodel';
import { PairScoreboardViewModel } from './pair-scoreboard.viewmodel';
import { PenaltyShootoutScoreboardViewModel } from './penalty-shootout-scoreboard.viewmodel';
import { PeriodGameScoreboardViewModel } from './period-game-scoreboard.viewmodel';
import { ScoreboardViewModel } from './scoreboard.viewmodel';
import { SetScoreboardViewModel, TennisScoreboardViewModel } from './set-scoreboard.viewmodel';
import { SnookerScoreboardViewModel } from './snooker-scoreboard.viewmodel';
import { SoccerScoreboardViewModel } from './soccer-scoreboard.viewmodel';
import { ScoreboardTournamentGameViewModel } from './tournament-scoreboard.viewmodel';

@Injectable({ providedIn: 'root' })
export class ScoreboardViewmodelFactory {
    constructor(
        private pointsVisibilityService: TennisPointsVisibilityService,
        private sitecore: Sitecore,
    ) {}

    createMyBetsScoreBoardViewModel(event: MyBetsEventModel): ScoreboardViewModel {
        const viewModel = this.getModel(event, !event.scoreboard.started);
        viewModel.fillFromEvent(event);

        return viewModel;
    }

    private getModel(event: EventModel | MyBetsEventModel, scoreboardNotStarted: boolean = false): ScoreboardViewModel {
        if (event.participants.length === 0 && event.scoreboard.type !== ScoreboardType.LeaderboardGame) {
            return new ScoreboardTournamentGameViewModel();
        }

        if (!event.live || scoreboardNotStarted) {
            if (event.scoreboard.type === ScoreboardType.SetGame) {
                return new SetScoreboardViewModel();
            }
            if (event.scoreboard.type !== ScoreboardType.TournamentGame) {
                return new PairScoreboardViewModel();
            }

            return new ScoreboardTournamentGameViewModel();
        }

        if (event.scoreboard.type !== ScoreboardType.TournamentGame && event.scoreboard.type !== ScoreboardType.LeaderboardGame) {
            switch (event.sport.id) {
                case SportConstant.Soccer: {
                    const score = event.scoreboard.getScore<FootballScore>();
                    if (score && score.penaltyShootOutScore) {
                        return new PenaltyShootoutScoreboardViewModel();
                    }

                    return new SoccerScoreboardViewModel();
                }
                case SportConstant.Darts: {
                    return new DartsScoreboardViewModel(this.sitecore);
                }
                case SportConstant.Tennis: {
                    const model = new TennisScoreboardViewModel();
                    model.arePointsAreVisible = !!event.league && this.pointsVisibilityService.canDisplayPoints(event.league.id);

                    return model;
                }
                case SportConstant.AmericanFootball:
                case SportConstant.Icehockey:
                case SportConstant.Basketball: {
                    const score = event.scoreboard.getScore<IcehockeyPeriodGameScore>();
                    if (score && score.penaltyShootOutScore) {
                        return new PenaltyShootoutScoreboardViewModel();
                    }

                    return new PeriodGameScoreboardViewModel();
                }
                case SportConstant.Snooker: {
                    return new SnookerScoreboardViewModel();
                }
                case SportConstant.Baseball: {
                    return new BaseBallscoreboardViewModel();
                }
                default: {
                    if (event.scoreboard.type === ScoreboardType.SetGame) {
                        return new SetScoreboardViewModel();
                    }

                    return new PairScoreboardViewModel();
                }
            }
        }
        switch (event.sport.id) {
            case SportConstant.Biathlon:
            case SportConstant.Motorbikes:
            case SportConstant.Formula1:
            case SportConstant.Skialpine: {
                return new ScoreboardLeaderboardGameViewModel();
            }
            default: {
                return new ScoreboardTournamentGameViewModel();
            }
        }
    }
}
